import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { downloadBlob } from '@/utils/download-file-from-link';
import { toast } from '@/utils/toast.ts';

import {
  bulkUpsertYearBudgets,
  deleteBudget,
  exportBudgetPdf,
  getBudgetById,
  getBudgets,
  updateBudget,
  upsertBudget,
} from './budget.api';
import type { BulkUpsertBudgetValues, UpdateBudgetValues, UpsertBudgetValues } from './budget.dto';

const BUDGETS_KEY = 'budgets';

/**
 * `enabled: false` keeps the request back where the caller may not hold `GetBudgets` —
 * budgets are a Command-tier right, and asking without it is a 403 rather than an empty page.
 */
export const useBudgetsQuery = (
  year?: number,
  {
    limit = 1000,
    page = 1,
    enabled = true,
  }: { limit?: number; page?: number; enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: [BUDGETS_KEY, year, limit, page],
    queryFn: () => getBudgets(page, limit, year),
    enabled,
  });

/**
 * Budgets also change outside the budget endpoints: deleting a transaction category hands
 * its plans to another one, summing the years both categories planned for.
 */
export const useInvalidateBudgets = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: [BUDGETS_KEY] }),
    [queryClient],
  );
};

export const useBudgetByIdQuery = (id: string | null | undefined) =>
  useQuery({
    queryKey: ['budget', id],
    queryFn: () => getBudgetById(id!),
    enabled: !!id,
  });

export const useUpsertBudgetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: UpsertBudgetValues) => upsertBudget(values),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [BUDGETS_KEY] });
    },
  });
};

export const useUpdateBudgetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: UpdateBudgetValues }) =>
      updateBudget(id, values),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: [BUDGETS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['budget', id] });
    },
  });
};

export const useDeleteBudgetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteBudget(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [BUDGETS_KEY] });
    },
  });
};

export const useExportBudgetPdfMutation = () =>
  useMutation({
    mutationFn: async (year: number) => {
      const blob = await exportBudgetPdf(year);
      await downloadBlob(blob, `budget-${year}.pdf`);
    },
    onSuccess() {
      toast.success('Budget report exported');
    },
    onError() {
      toast.error('Couldn’t export the budget report. Please try again.');
    },
  });

export const useBulkUpsertYearBudgetsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: BulkUpsertBudgetValues) => bulkUpsertYearBudgets(values),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [BUDGETS_KEY] });
    },
  });
};
